"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import FaqPreview from "@/components/FaqPreview";
import FaqSection from "@/components/FaqSection";

const steps = [
  {
    number: "01",
    title: "Complete the Application",
    summary: "Tell us about your family, home setup, and why you're ready for a Balinese companion.",
    highlights: [
      "Submit our online form in under 10 minutes",
      "Share photos of your living areas and any current pets",
      "Select your preferred coat colors and temperament",
      "Receive an instant confirmation email"
    ],
  },
  {
    number: "02",
    title: "Personalized Application Review",
    summary: "We'll reach out within 24 hours via your preferred method to provide a personalized review of your application, answer any questions, and confirm the best fit.",
    highlights: [
      "Friendly chat via video, phone, or email with our team",
      "Review care expectations and timeline",
      "Talk through upcoming litters and availability",
      "Get written approval with next steps"
    ],
  },
  {
    number: "03",
    title: "Reserve with Deposit",
    summary: "Secure your kitten with a $350 non-refundable deposit applied directly to the adoption fee.",
    highlights: [
      "Choose your kitten or pick a future litter",
      "Sign the simple reservation agreement",
      "Submit the $350 deposit to hold your spot",
      "Schedule pickup or flight nanny arrangements"
    ],
  },
  {
    number: "04",
    title: "Homecoming",
    summary: "Pick up in person or have us deliver. You’ll receive everything needed for a confident first week together.",
    highlights: [
      "Take home full health records and care plan",
      "Receive starter supplies and scent blanket",
      "Follow our day-by-day transition guide",
      "Stay connected with lifetime concierge support"
    ],
  },
];

const reservationFacts = [
  {
    title: "$400 Refundable Reservation Deposit",
description: "Deposit is fully **refundable** upon request before final adoption. Applied to your total adoption balance."
  },
  {
    title: "Transparent Adoption Fees",
    description: "Pet companions range from $500 – $1,200 depending on coat pattern and lineage. Breeding rights upgrade available by approval for +$400."
  },
  {
    title: "Pickup or Delivery",
    description: "Professional Courier Delivery Worldwide"
  },
];

const whatYouReceive = [
  "Licensed veterinarian exam, vaccinations, and parasite prevention",
  "Two rounds of deworming plus microchip registration",
  "Starter kit with premium food, toys, and familiar scent blanket",
  "Detailed care guide covering nutrition, grooming, and first-week schedule",
  "Early socialization with adults, children, and calm dogs",
  "30-day wellness assurance and lifetime concierge support",
];

const healthGuaranteeDetails = [
  {
    title: "Genetic & Congenital Assurance",
    description:
      "Every kitten leaves with a written two-year guarantee covering life-threatening genetic or congenital conditions that adversely impact quality of life.",
    points: [
      "Comprehensive veterinary exam, FeLV/FIV screening, and age-appropriate vaccinations are completed before release.",
      "If a licensed veterinarian diagnoses a covered inherited condition within 24 months, provide medical records within 7 days so we can coordinate care or offer a replacement kitten.",
      "Excludes issues caused by accidental injury, neglect, unauthorized breeding, or failure to follow our care protocol."
    ]
  },
  {
    title: "Wellness Timeline & Buyer Responsibilities",
    description:
      "To keep the guarantee in force, partner with us on timely exams and a consistent preventative routine tailored to Balinese sensitivities.",
    points: [
      "Schedule a wellness visit with your veterinarian within 72 hours of homecoming (excluding Sundays/holidays) and share the exam report with us.",
      "Maintain regular vaccinations, parasite prevention, and feed the approved high-quality diet listed in your take-home guide.",
      "Quarantine new pets for 10 days and supervise introductions to reduce stress-related illness or injury."
    ]
  },
  {
    title: "Support When Something Feels Off",
    description:
      "We offer concierge-level support to troubleshoot any concern—from appetite changes to litter habits—so potential issues are addressed quickly.",
    points: [
      "Call, text, or WhatsApp our wellness line for guidance; a breeder or veterinary liaison responds within 8 hours.",
      "Tele-vet triage is available to bridge late-night questions until you can see your preferred veterinarian.",
      "If a condition requires surgery, we partner with you on second opinions and recovery planning."
    ]
  }
];

const transportPolicies = [
  {
    title: "In-Person Collection at Our Nursery",
    description:
      "Meet us at our nursery for a relaxed handoff, review health records together, and receive an in-person transition orientation (plan for 45–60 minutes).",
    bullets: [
      "Bring a secure carrier, photo ID, and final payment confirmation.",
      "We provide a freshly packed travel kit with food, litter, toys, and scent blanket.",
      "If you’re driving more than four hours, we will map safe rest stops and hydration breaks."
    ]
  },
  {
    title: "Flight Nanny Service",
    description:
      "Our vetted flight nannies accompany kittens cabin-side from major regional airports to your nearest major airport.",
    bullets: [
      "Average cost $250–$300 including airfare; quoted at cost with receipts shared in your invoice portal.",
      "Nannies provide photo checkpoints at departure, mid-flight, and arrival, plus a written wellness summary.",
      "Pickup occurs curbside or in a pet relief area; please arrive 30 minutes prior to the scheduled landing." 
    ]
  },
  {
    title: "Weather & Contingency Planning",
    description:
      "Kitten safety comes first. We proactively adjust travel when extreme temperatures, airline disruptions, or health screenings require extra buffers.",
    bullets: [
      "If weather exceeds airline limits, we reschedule at no additional breeder fee and offer extended boarding at $35/day.",
      "Emergency delays trigger immediate updates via text and email; the kitten remains with a caregiver until safe transfer is possible.",
      "In the rare event a flight is cancelled after departure, the nanny escorts the kitten to an airport hotel and sends you real-time status updates with photos."
    ]
  }
];

export default function ProcessPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-24 pb-16">
        <div className="absolute inset-0 bg-gradient-to-r from-soft-gold/20 via-cream/50 to-warm-brown/15" />
        <div className="relative z-10 mx-auto max-w-5xl text-center space-y-6">
          <p className="inline-block rounded-full bg-white/80 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-soft-gold">
            Your Perfect Balinese Companion
          </p>
          <h1 className="font-heading text-4xl font-bold text-warm-brown sm:text-5xl md:text-6xl">
            Reserve with Ease, Welcome with Confidence
          </h1>
          <p className="text-base text-warm-brown/75 sm:text-lg max-w-3xl mx-auto">
            Loving homes create the brightest futures. Offer consistent care, affection, and a peaceful environment, and your kitten will return a lifetime of devotion.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm font-medium text-warm-brown/70">
            <span className="rounded-xl bg-white/80 px-4 py-2">Concierge-guided process</span>
            <span className="rounded-xl border border-soft-gold/30 px-4 py-2">Deposit applies to adoption</span>
            <span className="rounded-xl border border-soft-gold/30 px-4 py-2">Professional courier delivery worldwide</span>
          </div>
        </div>
      </section>

      {/* Steps */}
      <motion.section
        className="px-6 py-20"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-12">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Our Adoption Steps</h2>
            <p className="text-warm-brown/70 sm:text-lg max-w-3xl mx-auto">
              Four simple milestones guide you from application to a confirmed reservation and joyful homecoming.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className="rounded-3xl border border-sand/25 bg-white/85 p-8 shadow-sm backdrop-blur"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-warm-brown px-4 py-2 text-sm font-semibold text-white">Step {step.number}</div>
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-warm-brown">{step.title}</h3>
                      <p className="text-sm text-warm-brown/70">{step.summary}</p>
                    </div>
                    <ul className="space-y-1.5 text-sm text-warm-brown/70">
                      {step.highlights.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Reservation Facts */}
      <motion.section
        className="px-6 py-20 bg-white/55"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-10">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Reservation Essentials</h2>
            <p className="text-warm-brown/70 sm:text-lg max-w-3xl mx-auto">
              Clear pricing, a simple deposit, and flexible travel arrangements keep everything stress-free.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reservationFacts.map((fact) => (
              <div key={fact.title} className="rounded-3xl border border-sand/20 bg-white/85 p-6 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-warm-brown mb-3">{fact.title}</h3>
                <p className="text-sm text-warm-brown/70 leading-relaxed">{fact.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* What you receive */}
      <motion.section
        className="px-6 py-20"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-5xl space-y-8 text-center">
          <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">What Every Family Receives</h2>
          <p className="text-warm-brown/70 sm:text-lg max-w-3xl mx-auto">
            We handle the essentials so you can focus on bonding, cuddles, and creating new routines together.
          </p>
          <ul className="space-y-2 text-left text-sm text-warm-brown/75">
            {whatYouReceive.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.section>

      {/* Health Guarantee & Lifetime Support */}
      <motion.section
        className="px-6 py-20 bg-white/65"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-10">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Comprehensive Health Protection</h2>
            <p className="text-warm-brown/70 sm:text-lg max-w-3xl mx-auto">
              Genetics, congenital wellness, and day-to-day care are covered in writing so you can welcome your kitten with confidence.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {healthGuaranteeDetails.map((item) => (
              <div key={item.title} className="rounded-3xl border border-sand/25 bg-white/90 p-6 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-warm-brown mb-3">{item.title}</h3>
                <p className="text-sm text-warm-brown/70 leading-relaxed mb-3">{item.description}</p>
                <ul className="space-y-2 text-sm text-warm-brown/70">
                  {item.points.map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Transportation & Delivery */}
      <motion.section
        className="px-6 py-20"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-10">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Travel & Shipping Logistics</h2>
            <p className="text-warm-brown/70 sm:text-lg max-w-3xl mx-auto">
              Whether you visit our nursery or request a flight nanny, every detail is choreographed for safety and calm transitions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {transportPolicies.map((policy) => (
              <div key={policy.title} className="rounded-3xl border border-sand/25 bg-white/90 p-6 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-warm-brown mb-3">{policy.title}</h3>
                <p className="text-sm text-warm-brown/70 leading-relaxed mb-3">{policy.description}</p>
                <ul className="space-y-2 text-sm text-warm-brown/70">
                  {policy.bullets.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="rounded-3xl border border-soft-gold/30 bg-cream/70 p-6 text-sm text-warm-brown/70">
            <p className="font-semibold text-warm-brown">Travel Checklist</p>
            <p className="mt-2">
              Final invoices, nanny contact info, and pet health certificates are delivered through your client portal 72 hours before departure.
              We text live updates at takeoff, mid-journey, and arrival. If plans shift, we coordinate new travel immediately so you remain informed at every step.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Optional Training */}
      <motion.section
        className="px-6 pb-12"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-4xl rounded-3xl border border-soft-gold/25 bg-white/85 p-8 shadow-lg space-y-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Optional Concierge Training</h2>
          <p className="text-sm text-warm-brown/70">
            Extend your kitten's stay with our team for gentle litter training refreshers, travel prep, or socialization intensives. Weekly polishing sessions are available at $280 per week and can be tailored to your household needs.
          </p>
          <p className="text-sm text-warm-brown/70">
            Need personalized guidance after homecoming? Book virtual coaching calls for $120 each and receive action plans for introductions, routine setting, or enrichment ideas.
          </p>
        </div>
      </motion.section>

      {/* FAQ */}
      <motion.section
        className="px-6 py-16"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-4xl">
          <FaqPreview ctaHref="/faq" />
          <div className="mt-12">
            <FaqSection />
          </div>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="px-6 pb-24"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-5xl space-y-6 text-center">
          <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Ready to Reserve?</h2>
          <p className="text-warm-brown/70 sm:text-lg max-w-3xl mx-auto">
            Apply today and our concierge will contact you within 24 hours to confirm the deposit and finalize your reservation timeline.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <Link
              href="/kittens/application"
              className="inline-flex items-center justify-center rounded-xl bg-soft-gold px-8 py-4 text-base font-semibold text-warm-brown transition-transform hover:-translate-y-0.5 hover:bg-soft-gold/90"
            >
              Apply Now
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center rounded-xl border border-soft-gold/40 px-8 py-4 text-base font-semibold text-soft-gold transition-colors hover:bg-soft-gold/10"
            >
              Talk with Our Team
            </Link>
            <Link
              href="/waiting-list"
              className="inline-flex items-center justify-center rounded-xl border border-sand/40 px-8 py-4 text-base font-semibold text-warm-brown/70 transition-colors hover:bg-sand/15"
            >
              Join the Reservation List
            </Link>
          </div>
          <p className="text-xs text-warm-brown/60">
            $400 deposit required to reserve • Local pickup or professional courier delivery worldwide • Lifetime support included
          </p>
        </div>
      </motion.section>
    </main>
  );
}
